import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  type LegacyRobotState,
  type TaskCreateRequest,
  type LegacyTaskState,
} from '../rmf-client';
import { useSafeSiteId } from '../../state/useActiveSite';
import { getRuntimeConfig } from '../../config/runtime-config';
import { alertsKey, alertMutesKey, alertSummaryKey, METRICS_KEY } from '../scope';
import { withRetry } from '../retry';
import { getRequestBearerToken } from '../../auth/request-auth';
import { controlTask } from '../v1-client';
import { adaptAlert, type AlertViewModel } from '../adapters';
import type { SiteId, TaskControlRequest, TaskControlResponse } from '../v1';
import {
  fleetsQueryOptions,
  healthQueryOptions,
  readinessQueryOptions,
  robotsQueryOptions,
  tasksQueryOptions,
} from '../query-options';

function asSiteId(siteId: string): SiteId | null {
  return siteId ? (siteId as SiteId) : null;
}

export { useLiveData } from './useLiveData';

export function useHealth() {
  return useQuery(healthQueryOptions());
}

export function useReadiness() {
  // /ready returns the full component-level readiness check (zenoh,
  // rmf_graph, db). /health is a liveness probe that returns a plain
  // {status:"ok"} — not the right shape for the readiness card. The
  // 30 s interval matches the global backend-status poller.
  return useQuery(readinessQueryOptions());
}

export function useFleets() {
  const siteId = useSafeSiteId();
  return useQuery(fleetsQueryOptions(asSiteId(siteId)));
}

export function useRobots() {
  const siteId = useSafeSiteId();
  return useQuery<LegacyRobotState[]>(robotsQueryOptions(asSiteId(siteId)));
}

export function useTasks() {
  const siteId = useSafeSiteId();
  return useQuery<LegacyTaskState[]>(tasksQueryOptions(asSiteId(siteId)));
}

/**
 * Alerts — read from the real backend evaluator (`backend/api_server
 * /alerts_engine.py`, Workstream C1) via `GET /api/v1/sites/{site}
 * /alerts`, replacing the earlier client-side derivation from
 * `useRobots`/`useDoors`/`useLifts`. That derivation is what the
 * backend evaluator itself now ports (verified rule-for-rule against
 * the live wire contract — see `alerts_engine.py`'s docstring), so
 * alerts now persist server-side, survive a closed browser tab, and
 * support ack/resolve (Workstream C2) shared across every operator
 * viewing the site — the client-derived version had none of that,
 * since there was nowhere to persist it to.
 *
 * Polls (`refetchInterval`) rather than a live v1 SSE category: alerts
 * aren't in the v1 `CategoryName` enum, and adding one would mean new
 * manifest/tombstone infrastructure for comparatively little gain over
 * a 15s poll on a low-cardinality, already-low-latency-tolerant screen.
 *
 * Populates the SAME `alertsKey(siteId)` cache slot the notification
 * bell/toast provider (`state/alert-notifications/provider.tsx`)
 * already reads from — that provider needs zero changes, it was
 * already built to read raw alert rows from this cache key and adapt
 * them itself.
 */
export function useAlerts() {
  const siteId = useSafeSiteId();
  const query = useQuery<unknown[]>({
    queryKey: alertsKey(siteId),
    queryFn: async () => {
      if (!siteId) return [];
      const token = getRequestBearerToken();
      const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
      const url = `${getRuntimeConfig().apiBaseUrl}/api/v1/sites/${encodeURIComponent(siteId)}/alerts?state=open`;
      const res = await fetch(url, { cache: 'no-store', headers });
      const body = await readAlertJson<{ alerts?: unknown }>(res, 'alerts');
      if (!Array.isArray(body.alerts)) {
        throw new Error('Alerts: the server answered without an alerts list');
      }
      return body.alerts;
    },
    refetchInterval: 15_000,
    staleTime: 10_000,
  });
  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
}

/**
 * `GET /api/v1/sites/{site}/alerts/summary` — the server-side
 * `count(*)` of OPEN alert rows, for the top-bar badge. Its own
 * endpoint + cache slot rather than `useAlerts().data.length`
 * because the list endpoint is a capped page: the array length
 * saturates at the page limit and stops being a count. Polls on
 * the same 15 s cadence as the list so badge and screen move
 * together.
 */
export function useAlertSummary() {
  const siteId = useSafeSiteId();
  return useQuery<AlertSummary>({
    queryKey: alertSummaryKey(siteId),
    queryFn: async () => {
      if (!siteId) return { open: 0 };
      const token = getRequestBearerToken();
      const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
      const url = `${getRuntimeConfig().apiBaseUrl}/api/v1/sites/${encodeURIComponent(siteId)}/alerts/summary`;
      const res = await fetch(url, { cache: 'no-store', headers });
      // A failure is an ERROR, never a zero: the badge shows "?" for it
      // and the attention list reads "Can't check: alerts". This used to
      // swallow every failure into `{ open: 0 }`, which a calm screen
      // cannot tell from a site with nothing open.
      const body = await readAlertJson<{ open?: unknown }>(res, 'alert summary');
      if (typeof body.open !== 'number' || !Number.isFinite(body.open)) {
        throw new Error('Alert summary: the server answered without an open count');
      }
      return { open: body.open };
    },
    refetchInterval: 15_000,
    staleTime: 10_000,
  });
}

export interface AlertSummary {
  open: number;
}

/**
 * One alert, as `useAlert` reads it. Each answer is its own state, so
 * nothing downstream has to tell them apart by poking at an error:
 *
 * - `found`: 200. The row may be closed (`isOpen` false); the endpoint
 *   serves closed rows as well as open ones. `refreshError` is null while
 *   the latest read succeeded. Once a re-read fails it carries why, and
 *   `alert` is then the last row read, not the current one: a consumer
 *   says so (could not check) rather than show it as live.
 * - `gone`: 404. No row with this id at this site. Alert ids are
 *   `alert_<uuid4 hex>` and never reused, so the answer will not change.
 * - `forbidden`: 403. This login may not read this site's alerts.
 * - `error`: anything else (a 5xx, a network failure, a body that is
 *   not an alert), and nothing better is known yet.
 * - `idle`: no site or no id, so nothing was asked.
 */
export type AlertLookup =
  | { state: 'idle' }
  | { state: 'loading' }
  | { state: 'found'; alert: AlertViewModel; refreshError: Error | null }
  | { state: 'gone' }
  | { state: 'forbidden' }
  | { state: 'error'; error: Error };

/** What one read answers, as cached. `refreshError` is not part of it:
 *  it is the query's error beside the cached row, joined on the way out. */
type AlertAnswer =
  | { state: 'found'; alert: AlertViewModel }
  | Extract<AlertLookup, { state: 'gone' | 'forbidden' }>;

/** The cache slot for one alert. NOT under `alertsKey`: the
 *  notification provider treats every `['alerts', site]` update as the
 *  row LIST, and a single row there would empty the bell. */
export const alertByIdKey = (siteId: string, alertId: string) =>
  ['alert', siteId, alertId] as const;

/**
 * `GET /api/v1/sites/{site}/alerts/{id}`: one alert, by id. What a link
 * naming an alert (`focus=alert:<id>`) resolves through.
 *
 * Polls while the row is found, so its ack and close show up; a `gone`
 * or `forbidden` answer is not polled (a later mount past the stale time
 * asks once more, as any query does). Like the list, the last good answer
 * is kept beside a failed refetch rather than replaced by the error; on a
 * found row the failure rides along as `refreshError`, so the row can be
 * shown as last known. A `gone` answer cannot go stale: the id is never
 * reused.
 */
export function useAlert(alertId: string | null | undefined): AlertLookup {
  const siteId = useSafeSiteId();
  const id = alertId ?? '';
  const enabled = Boolean(siteId && id);
  const query = useQuery<AlertAnswer>({
    queryKey: alertByIdKey(siteId, id),
    enabled,
    queryFn: async () => {
      const token = getRequestBearerToken();
      const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
      const url = `${getRuntimeConfig().apiBaseUrl}/api/v1/sites/${encodeURIComponent(siteId)}/alerts/${encodeURIComponent(id)}`;
      const res = await fetch(url, { cache: 'no-store', headers });
      if (res.status === 404) return { state: 'gone' };
      if (res.status === 403) return { state: 'forbidden' };
      const body = await readAlertJson<unknown>(res, `alert ${id}`);
      if (body === null || typeof body !== 'object' || Array.isArray(body)) {
        throw new Error(`Alert ${id}: the server answered without an alert`);
      }
      return { state: 'found', alert: adaptAlert(body) };
    },
    refetchInterval: (q) => (q.state.data?.state === 'found' ? 15_000 : false),
    staleTime: 10_000,
  });
  const data = query.data;
  const refreshError = data?.state === 'found' && query.isError ? query.error : null;
  // One object per (row, failure), so a consumer's effect on the lookup
  // does not re-run on every render.
  const found = useMemo<AlertLookup | null>(
    () => (data?.state === 'found' ? { ...data, refreshError } : null),
    [data, refreshError],
  );
  if (!enabled) return { state: 'idle' };
  if (found) return found;
  if (data && data.state !== 'found') return data;
  if (query.isError) return { state: 'error', error: query.error };
  return { state: 'loading' };
}

/**
 * The body of an alerts read, or a THROWN error saying why there is none.
 *
 * Both alert hooks used to turn a non-OK status or a non-JSON body (a
 * proxy's HTML error page, an expired session's login redirect) into an
 * empty list and a zero count. That reads exactly like a quiet site, so
 * nothing downstream could say "can't check" -- the honest answer when
 * the read failed. TanStack keeps the last good data beside the error,
 * so a blip does not empty a list that was showing rows.
 */
async function readAlertJson<T>(res: Response, what: string): Promise<T> {
  if (!res.ok) {
    throw httpError(res.status, `Failed to read ${what}: HTTP ${res.status}`);
  }
  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    throw httpError(
      res.status,
      `Failed to read ${what}: the server answered ${contentType || 'without a content type'}, not JSON`,
    );
  }
  try {
    return (await res.json()) as T;
  } catch {
    throw httpError(res.status, `Failed to read ${what}: the body is not valid JSON`);
  }
}

/** One per-device alert mute, as served by `GET .../alerts/mutes`. */
export interface AlertMuteOut {
  id: number;
  site: string;
  source_kind: string;
  source_id: string;
  reason: string | null;
  created_by: string | null;
  created_at_ms: number;
  /** `null` = until unmuted. */
  expires_at_ms: number | null;
}

export interface BulkResolveResult {
  resolved: number;
  /**
   * `id` is a STRING, like every alert id: `alerts.alert_id` is a
   * `String(64)` primary key holding `alert_{uuid4hex}`, not a serial.
   * (Mute ids, by contrast, really are integers — `alert_mutes.id` is a
   * serial — so the two are deliberately different types here.)
   */
  skipped: Array<{ id: string; reason: string }>;
}

export interface CreateAlertMuteRequest {
  source_kind: string;
  source_id: string;
  reason?: string;
  /** Omit for "until unmuted". */
  ttl_ms?: number;
}

export interface CreateAlertMuteResult {
  mute: AlertMuteOut;
  /** Open alerts on the device that the server swept closed. */
  resolved: number;
}

/** Every cache slot an alert-state mutation can change: the row list,
 *  the badge count, every single-alert read (`useAlert`), and (for
 *  mute/unmute) the mutes list. Invalidated together so the badge can
 *  never disagree with the screen after an operator action. */
function invalidateAlertQueries(qc: ReturnType<typeof useQueryClient>, siteId: string): void {
  void qc.invalidateQueries({ queryKey: alertsKey(siteId) });
  void qc.invalidateQueries({ queryKey: alertSummaryKey(siteId) });
  void qc.invalidateQueries({ queryKey: ['alert', siteId] });
}

/** Acknowledge an alert — any site-assigned role (operational, not config). */
export function useAckAlert() {
  const qc = useQueryClient();
  const siteId = useSafeSiteId();
  return useMutation({
    mutationFn: (alertId: string) =>
      withRetry(async (key) => {
        const url = `${getRuntimeConfig().apiBaseUrl}/api/v1/sites/${encodeURIComponent(siteId)}/alerts/${encodeURIComponent(alertId)}/ack`;
        const res = await authedJsonFetch(url, {}, key);
        if (!res.ok) throw httpError(res.status, `Failed to ack alert ${alertId}: HTTP ${res.status}`);
        return res.json();
      }),
    onSuccess: () => invalidateAlertQueries(qc, siteId),
  });
}

/** Resolve an alert (force-closes it even if the underlying condition
 *  hasn't cleared yet — see `alerts_engine.py::AlertsEngine.forget`). */
export function useResolveAlert() {
  const qc = useQueryClient();
  const siteId = useSafeSiteId();
  return useMutation({
    mutationFn: (alertId: string) =>
      withRetry(async (key) => {
        const url = `${getRuntimeConfig().apiBaseUrl}/api/v1/sites/${encodeURIComponent(siteId)}/alerts/${encodeURIComponent(alertId)}/resolve`;
        const res = await authedJsonFetch(url, {}, key);
        if (!res.ok) throw httpError(res.status, `Failed to resolve alert ${alertId}: HTTP ${res.status}`);
        return res.json();
      }),
    onSuccess: () => invalidateAlertQueries(qc, siteId),
  });
}

/**
 * `POST .../alerts/bulk_resolve` `{ids}` → `{resolved, skipped}`.
 * The server resolves what it can and reports the rest per-id (fire
 * alerts, already-closed rows) instead of failing the whole batch, so
 * callers must surface `skipped` rather than assuming all-or-nothing.
 */
export function useBulkResolveAlerts() {
  const qc = useQueryClient();
  const siteId = useSafeSiteId();
  return useMutation<BulkResolveResult, Error, string[]>({
    mutationFn: (ids) =>
      withRetry(async (key) => {
        const url = `${getRuntimeConfig().apiBaseUrl}/api/v1/sites/${encodeURIComponent(siteId)}/alerts/bulk_resolve`;
        const res = await authedJsonFetch(url, { ids }, key);
        if (!res.ok) throw httpError(res.status, `Failed to resolve ${ids.length} alerts: HTTP ${res.status}`);
        return res.json() as Promise<BulkResolveResult>;
      }),
    onSuccess: () => invalidateAlertQueries(qc, siteId),
  });
}

/**
 * The site's per-device alert mutes (`GET .../alerts/mutes`). Slow
 * 60 s poll: mutes change only on explicit operator action, and every
 * mutating hook below invalidates this slot, so the poll is just a
 * backstop for another operator's edits.
 */
export function useAlertMutes() {
  const siteId = useSafeSiteId();
  return useQuery<AlertMuteOut[]>({
    queryKey: alertMutesKey(siteId),
    queryFn: async () => {
      if (!siteId) return [];
      const token = getRequestBearerToken();
      const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
      const url = `${getRuntimeConfig().apiBaseUrl}/api/v1/sites/${encodeURIComponent(siteId)}/alerts/mutes`;
      const res = await fetch(url, { cache: 'no-store', headers });
      const contentType = res.headers.get('content-type') ?? '';
      if (!res.ok || !contentType.includes('application/json')) return [];
      const body = (await res.json()) as unknown;
      return Array.isArray(body) ? (body as AlertMuteOut[]) : [];
    },
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

/**
 * `POST .../alerts/mutes` — upserts a per-device mute AND sweeps the
 * device's open alert rows closed server-side (the `resolved` count in
 * the response), which is why this invalidates the alert list/summary
 * too, not just the mutes list.
 */
export function useCreateAlertMute() {
  const qc = useQueryClient();
  const siteId = useSafeSiteId();
  return useMutation<CreateAlertMuteResult, Error, CreateAlertMuteRequest>({
    mutationFn: (req) =>
      withRetry(async (key) => {
        const url = `${getRuntimeConfig().apiBaseUrl}/api/v1/sites/${encodeURIComponent(siteId)}/alerts/mutes`;
        const res = await authedJsonFetch(url, req, key);
        if (!res.ok)
          throw httpError(res.status, `Failed to mute ${req.source_kind} ${req.source_id}: HTTP ${res.status}`);
        return res.json() as Promise<CreateAlertMuteResult>;
      }),
    onSuccess: () => {
      invalidateAlertQueries(qc, siteId);
      void qc.invalidateQueries({ queryKey: alertMutesKey(siteId) });
    },
  });
}

/** `DELETE .../alerts/mutes/{id}` (204). Unmuting re-arms the engine:
 *  a still-bad device re-opens an alert on its next sample. */
export function useDeleteAlertMute() {
  const qc = useQueryClient();
  const siteId = useSafeSiteId();
  return useMutation<void, Error, number>({
    mutationFn: (muteId) =>
      withRetry(async (key) => {
        const url = `${getRuntimeConfig().apiBaseUrl}/api/v1/sites/${encodeURIComponent(siteId)}/alerts/mutes/${encodeURIComponent(String(muteId))}`;
        const res = await authedJsonFetch(url, undefined, key, 'DELETE');
        // 404 IS SUCCESS HERE. Mutes carry a TTL and the engine reaps
        // expired ones on its own sweep (`alerts_engine.py`), so the row
        // the operator is looking at can be gone between the render that
        // drew the Unmute button and the click on it — likewise if
        // another operator unmuted the same device first. In every one of
        // those cases the DESIRED END STATE (this device is not muted)
        // already holds, and `api/alerts.py`'s 404 was reported to the
        // operator as "Failed to unmute" over a device that is, in fact,
        // no longer muted. Fall through to `onSuccess` so the list
        // refetches and the row simply disappears.
        if (res.status === 404) return;
        if (!res.ok) throw httpError(res.status, `Failed to unmute: HTTP ${res.status}`);
      }),
    onSuccess: () => {
      invalidateAlertQueries(qc, siteId);
      void qc.invalidateQueries({ queryKey: alertMutesKey(siteId) });
    },
  });
}

/**
 * Build a fetch call that carries the JWT bearer token plus an
 * `X-Idempotency-Key` so the api-server can dedupe retries.
 *
 * The header name must stay `X-Idempotency-Key` — the exact spelling
 * in the api-server's CORS `allow_headers` (and what `v1-client.ts`
 * sends). The bare `idempotency-key` this used to send was a
 * DIFFERENT allowlist entry, so the browser preflight was rejected
 * 400 before any route ran and every ack/resolve surfaced as
 * `TypeError: Failed to fetch` (retried 3× by `withRetry`, all
 * doomed). See `retry.ts::idempotencyHeaders` for the same rule.
 *
 * `body === undefined` sends no body at all (e.g. DELETE), not the
 * string `"undefined"`.
 */
async function authedJsonFetch(
  url: string,
  body: unknown,
  idempotencyKey: string,
  method: 'POST' | 'DELETE' = 'POST',
): Promise<Response> {
  const token = getRequestBearerToken();
  const headers: HeadersInit = {
    'content-type': 'application/json',
    'X-Idempotency-Key': idempotencyKey,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
  return fetch(url, {
    method,
    headers,
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
}

function httpError(status: number, msg: string, body?: unknown): Error & { status: number; body?: unknown } {
  const err = new Error(msg) as Error & { status: number; body?: unknown };
  err.status = status;
  if (body !== undefined) err.body = body;
  return err;
}

async function safeJson(res: Response): Promise<unknown | undefined> {
  const text = await res.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

export function useCreateTask() {
  const qc = useQueryClient();
  const siteId = useSafeSiteId();
  const sendSiteId = getRuntimeConfig().featureFlags.siteIdInWriteBodies;
  return useMutation({
    mutationFn: (req: TaskCreateRequest) =>
      withRetry(async (key) => {
        const { rmfEndpoints } = await import('../endpoints');
        const res = await authedJsonFetch(
          rmfEndpoints.tasks(),
          sendSiteId ? { ...req, siteId: siteId } : req,
          key,
        );
        if (!res.ok) throw httpError(res.status, `Failed to create task: HTTP ${res.status}`);
        return res.json();
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: tasksQueryOptions(asSiteId(siteId)).queryKey }),
  });
}

/**
 * Dispatch a task using the upstream `POST /tasks/dispatch_task` shape.
 * The accepted body conforms to the upstream `DispatchTaskRequest`:
 *   { type, description, phases, unix_millis_earliest_start_time, assigned_to }
 * plus the SPA's optional `siteId` for site scoping.
 */
export function useDispatchTask() {
  const qc = useQueryClient();
  const siteId = useSafeSiteId();
  const sendSiteId = getRuntimeConfig().featureFlags.siteIdInWriteBodies;
  return useMutation({
    mutationFn: (req: {
      type: string;
      description?: string;
      phases?: unknown[];
      unix_millis_earliest_start_time?: number;
      unix_millis_finish_time?: number;
      assigned_to?: { group?: string; name?: string };
      priority?: number;
    }) =>
      withRetry(async (key) => {
        const { rmfEndpoints } = await import('../endpoints');
        const body = {
          ...req,
          ...(sendSiteId ? { siteId: siteId } : {}),
        };
        const res = await authedJsonFetch(rmfEndpoints.dispatchTask(), body, key);
        if (!res.ok) {
          const errBody = await safeJson(res);
          throw httpError(res.status, `Failed to dispatch task: HTTP ${res.status}`, errBody);
        }
        return res.json();
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: tasksQueryOptions(asSiteId(siteId)).queryKey }),
  });
}

/** Cancel a task — `POST /tasks/cancel_task` with `{type, task_id}`. */
export function useCancelTask() {
  const qc = useQueryClient();
  const siteId = useSafeSiteId();
  return useMutation({
    mutationFn: (taskId: string) =>
      withRetry(async (key) => {
        const { rmfEndpoints } = await import('../endpoints');
        const res = await authedJsonFetch(
          rmfEndpoints.taskCancel(),
          { type: 'cancel_task_request', task_id: taskId },
          key,
        );
        if (!res.ok)
          throw httpError(res.status, `Failed to cancel task ${taskId}: HTTP ${res.status}`);
        return res.json();
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: tasksQueryOptions(asSiteId(siteId)).queryKey }),
  });
}

/**
 * `POST /api/v1/sites/{site}/tasks/{task}/control` (plan §Command
 * lifecycle) — the full `TaskControlRequest.action` set the backend
 * already accepts (cancel/interrupt/kill/resume/rewind/skip_phase/
 * undo_phase), not just cancel. Callers gate which actions are
 * offered using `SiteCapabilities` (`capabilitiesQueryOptions`).
 */
export function useTaskControl() {
  const qc = useQueryClient();
  const siteId = useSafeSiteId();
  return useMutation<TaskControlResponse, Error, { taskId: string; action: TaskControlRequest['action']; phaseId?: string }>(
    {
      mutationFn: ({ taskId, action, phaseId }) =>
        controlTask(siteId as SiteId, { site: siteId as SiteId, task: taskId }, {
          action,
          phase_id: phaseId,
          idempotency_key: crypto.randomUUID(),
        }),
      onSuccess: () => qc.invalidateQueries({ queryKey: tasksQueryOptions(asSiteId(siteId)).queryKey }),
    },
  );
}

/**
 * `DELETE /api/v1/sites/{site}/tasks/{task}/history` (204) — erase a
 * FINISHED task's record everywhere the console keeps one: the live
 * projection the Tasks tab reads, its `task_completions` row (Metrics)
 * and its `task_history` lines (the drawer Timeline). The support
 * plane's `issues` row deliberately survives.
 *
 * Deliberately NOT `task.retire`, which is the opposite operation:
 * retire *manufactures* a terminal record for an orphaned task and is
 * platform_admin-only. This one *erases* one, and the server refuses a
 * non-terminal task with 409 — a live task can be cancelled, never
 * deleted. Callers gate the control on `TASK_DELETE_HISTORY_ROLES`
 * (`auth/types.ts`); a 403 here means that list and the backend's
 * `policy/capabilities.py` have drifted.
 *
 * The row leaves the table on its own: the server publishes a task
 * tombstone, which `fetch-sse.ts` classifies and `category-reducers.ts`
 * applies by deleting the id from the cache. The invalidation below is
 * the belt to that braces — it makes the tab converge even for a
 * viewer whose live stream is down — NOT a hand-removal, which would
 * fight the reducer. Metrics has no such live path at all, so for that
 * cache the invalidation is the ONLY thing that makes the screen stop
 * counting a task the dialog said was erased from it.
 */
export function useDeleteTaskHistory() {
  const qc = useQueryClient();
  const siteId = useSafeSiteId();
  return useMutation<void, Error, string>({
    mutationFn: (taskId) =>
      withRetry(async (key) => {
        const url = `${getRuntimeConfig().apiBaseUrl}/api/v1/sites/${encodeURIComponent(siteId)}/tasks/${encodeURIComponent(taskId)}/history`;
        const res = await authedJsonFetch(url, undefined, key, 'DELETE');
        if (!res.ok)
          throw httpError(
            res.status,
            `Failed to delete task ${taskId} from history: HTTP ${res.status}`,
            await safeJson(res),
          );
      }),
    onSuccess: () => {
      // The TASK LIST's real cache slot — `tasksQueryOptions(...)`'s v1
      // key, which is what `useTasks()` actually reads. This used to
      // invalidate `tasksKey(siteId)` (`['tasks', siteId]`), a key NO
      // query in the app is registered under, so the "belt to the
      // braces" described above did not exist: a viewer whose live
      // stream is down (no tombstone event) kept the deleted row on
      // screen until something else refetched.
      void qc.invalidateQueries({ queryKey: tasksQueryOptions(asSiteId(siteId)).queryKey });
      // METRICS TOO, and this one is a promise the UI makes out loud:
      // the confirm dialog tells the operator the task is erased from
      // Metrics, and the server does delete its `task_completions` row —
      // but the Metrics screen kept serving a cached window that still
      // counted it. Invalidate by the `[METRICS_KEY, siteId]` PREFIX,
      // not `metricsKey(siteId, days, granularity)`: every `days` /
      // `granularity` combination the operator has visited is a separate
      // cache entry and every one of them is now wrong.
      void qc.invalidateQueries({ queryKey: [METRICS_KEY, siteId] });
    },
  });
}

